import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { PendingUpload, UploadStatus, ContentRating } from '../types/uploads';
import {
  Upload,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  FileVideo,
  Loader2,
  Edit2,
  Trash2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const RATINGS: ContentRating[] = ['G', 'PG', 'PG-13', 'R', 'NC-17', 'TV-Y', 'TV-G', 'TV-PG', 'TV-14', 'TV-MA'];

interface EditForm {
  title: string;
  description: string;
  language: string;
  rating: ContentRating;
  runtime_minutes: string;
}

const statusConfig: Record<UploadStatus, { label: string; icon: any; variant: any }> = {
  draft: { label: 'Draft', icon: Clock, variant: 'secondary' },
  uploading: { label: 'Uploading', icon: Loader2, variant: 'default' },
  scanning: { label: 'Scanning', icon: Loader2, variant: 'default' },
  human_review: { label: 'In Review', icon: Eye, variant: 'default' },
  approved: { label: 'Approved', icon: CheckCircle2, variant: 'success' },
  rejected: { label: 'Rejected', icon: XCircle, variant: 'destructive' },
  ingesting: { label: 'Processing', icon: Loader2, variant: 'default' },
  ingested: { label: 'Ready', icon: CheckCircle2, variant: 'success' },
  error: { label: 'Error', icon: AlertCircle, variant: 'destructive' },
};

export default function ContentUploads() {
  const navigate = useNavigate();
  const { publisher } = useAuth();
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | UploadStatus>('all');

  // Delete
  const [deleteUploadId, setDeleteUploadId] = useState<string | null>(null);

  // Edit metadata
  const [editUpload, setEditUpload] = useState<PendingUpload | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '', language: '', rating: 'G', runtime_minutes: '' });
  const [saving, setSaving] = useState(false);

  // Rejection / appeal
  const [detailUpload, setDetailUpload] = useState<PendingUpload | null>(null);
  const [appealReason, setAppealReason] = useState('');
  const [appealSubmitting, setAppealSubmitting] = useState(false);

  useEffect(() => {
    if (!publisher?.id) return;

    fetchUploads();

    // Set up realtime subscription for upload updates
    const channel = supabase
      .channel('pending_uploads_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_uploads',
        },
        () => {
          fetchUploads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [publisher?.id]);

  const fetchUploads = async () => {
    if (!publisher?.id) return;
    try {
      const { data, error } = await supabase
        .from('pending_uploads')
        .select('*')
        .eq('publisher_id', publisher.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUploads((data || []) as unknown as PendingUpload[]);
    } catch (error: any) {
      console.error('Error fetching uploads:', error);
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUploadId) return;
    try {
      const { error } = await supabase.from('pending_uploads').delete().eq('id', deleteUploadId);
      if (error) throw error;
      setUploads(prev => prev.filter(u => u.id !== deleteUploadId));
      toast.success('Upload deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete upload');
    } finally {
      setDeleteUploadId(null);
    }
  };

  const openEdit = (upload: PendingUpload) => {
    setEditUpload(upload);
    setEditForm({
      title: upload.metadata.title ?? '',
      description: upload.metadata.description ?? '',
      language: upload.metadata.language ?? '',
      rating: upload.metadata.rating ?? 'G',
      runtime_minutes: upload.metadata.runtime_claimed ? String(Math.round(upload.metadata.runtime_claimed / 60)) : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editUpload) return;
    if (!editForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const runtimeSeconds = editForm.runtime_minutes ? parseInt(editForm.runtime_minutes) * 60 : undefined;
      const updatedMetadata = {
        ...editUpload.metadata,
        title: editForm.title.trim(),
        description: editForm.description.trim() || undefined,
        language: editForm.language.trim() || editUpload.metadata.language,
        rating: editForm.rating,
        ...(runtimeSeconds !== undefined ? { runtime_claimed: runtimeSeconds } : {}),
      };
      const { error } = await supabase
        .from('pending_uploads')
        .update({ metadata: updatedMetadata })
        .eq('id', editUpload.id);
      if (error) throw error;
      setUploads(prev => prev.map(u => u.id === editUpload.id ? { ...u, metadata: updatedMetadata } : u));
      toast.success('Metadata updated');
      setEditUpload(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update metadata');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: UploadStatus) => {
    const config = statusConfig[status];
    const Icon = config.icon;
    const isAnimated = ['uploading', 'scanning', 'ingesting'].includes(status);

    return (
      <Badge variant={config.variant as any}>
        <Icon className={`mr-1 h-3 w-3 ${isAnimated ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  const filteredUploads = filter === 'all'
    ? uploads
    : uploads.filter(u => u.upload_status === filter);

  const getStatusMessage = (upload: PendingUpload) => {
    switch (upload.upload_status) {
      case 'draft':
        return 'Ready to upload video file';
      case 'uploading':
        return 'File upload in progress...';
      case 'scanning':
      case 'human_review':
        return 'Your content has been submitted for review';
      case 'approved':
        return 'Content approved! Processing for streaming...';
      case 'rejected':
        return 'Content needs revision. Click to view details.';
      case 'ingesting':
        return 'Being prepared for streaming. Will be available soon.';
      case 'ingested':
        return 'Content is live and available on the platform!';
      case 'error':
        return 'An error occurred. Please try again or contact support.';
      default:
        return '';
    }
  };

  const handleRowClick = (upload: PendingUpload) => {
    if (upload.upload_status === 'draft') {
      navigate(`/content/upload/${upload.id}`);
    } else if (upload.upload_status === 'rejected') {
      setDetailUpload(upload);
      setAppealReason('');
    }
  };

  const handleSubmitAppeal = async () => {
    if (!detailUpload || !appealReason.trim()) {
      toast.error('Please describe why you are appealing this decision');
      return;
    }
    setAppealSubmitting(true);
    try {
      const { error } = await supabase
        .from('pending_uploads')
        .update({
          appeal_status: 'requested',
          metadata: {
            ...detailUpload.metadata,
            appeal_reason: appealReason.trim(),
            appeal_submitted_at: new Date().toISOString(),
          },
        })
        .eq('id', detailUpload.id);
      if (error) throw error;
      setUploads(prev =>
        prev.map(u =>
          u.id === detailUpload.id
            ? { ...u, appeal_status: 'requested' as const, metadata: { ...u.metadata, appeal_reason: appealReason.trim() } }
            : u
        )
      );
      toast.success('Appeal submitted — our team will review it');
      setDetailUpload(null);
      setAppealReason('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit appeal');
    } finally {
      setAppealSubmitting(false);
    }
  };

  const getStatusCounts = () => {
    return {
      all: uploads.length,
      draft: uploads.filter(u => u.upload_status === 'draft').length,
      uploading: uploads.filter(u => u.upload_status === 'uploading').length,
      scanning: uploads.filter(u => ['scanning', 'human_review'].includes(u.upload_status)).length,
      approved: uploads.filter(u => ['approved', 'ingesting', 'ingested'].includes(u.upload_status)).length,
      rejected: uploads.filter(u => u.upload_status === 'rejected').length,
    };
  };

  const counts = getStatusCounts();

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Content Uploads</h1>
          <p className="text-muted-foreground mt-2">
            Manage your uploaded content and track review status
          </p>
        </div>
        <Button onClick={() => navigate('/content/new')}>
          <Upload className="mr-2 h-4 w-4" />
          New Upload
        </Button>
      </div>

      {/* Status Filter Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        <Card
          className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">All</p>
            <p className="text-2xl font-bold">{counts.all}</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${filter === 'draft' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter('draft')}
        >
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Drafts</p>
            <p className="text-2xl font-bold">{counts.draft}</p>
          </CardContent>
        </Card>

        <Card className="cursor-not-allowed opacity-60">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Uploading</p>
            <p className="text-2xl font-bold">{counts.uploading}</p>
          </CardContent>
        </Card>

        <Card className="cursor-not-allowed opacity-60">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">In Review</p>
            <p className="text-2xl font-bold">{counts.scanning}</p>
          </CardContent>
        </Card>

        <Card className="cursor-not-allowed opacity-60">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-green-600">{counts.approved}</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Rejected</p>
            <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
          </CardContent>
        </Card>
      </div>

      {/* Uploads Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Uploads</CardTitle>
          <CardDescription>
            {filter === 'all' ? 'All uploads' : `Showing ${filter} uploads`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredUploads.length === 0 ? (
            <div className="text-center py-12">
              <FileVideo className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No uploads yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Get started by uploading your first piece of content
              </p>
              <Button className="mt-4" onClick={() => navigate('/content/new')}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Content
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUploads.map((upload) => {
                  const canEdit = upload.upload_status === 'draft';
                  const canDelete = upload.upload_status === 'draft' || upload.upload_status === 'error';
                  return (
                    <TableRow
                      key={upload.id}
                      className={canEdit || upload.upload_status === 'rejected' ? 'cursor-pointer hover:bg-muted/50' : ''}
                      onClick={() => handleRowClick(upload)}
                    >
                      <TableCell className="font-medium">
                        {upload.metadata.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{upload.content_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(upload.upload_status)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(upload.created_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-sm">
                        {getStatusMessage(upload)}
                      </TableCell>
                      <TableCell>
                        {(canEdit || canDelete) && (
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openEdit(upload)}
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => setDeleteUploadId(upload.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Metadata Dialog */}
      <Dialog open={!!editUpload} onOpenChange={open => !open && setEditUpload(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Metadata</DialogTitle>
            <DialogDescription>Update the metadata for this upload.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Content title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description"
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Input
                  value={editForm.language}
                  onChange={e => setEditForm(f => ({ ...f, language: e.target.value }))}
                  placeholder="en"
                />
              </div>
              <div className="space-y-2">
                <Label>Rating</Label>
                <Select value={editForm.rating} onValueChange={v => setEditForm(f => ({ ...f, rating: v as ContentRating }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATINGS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Runtime (minutes)</Label>
              <Input
                type="number"
                min="1"
                value={editForm.runtime_minutes}
                onChange={e => setEditForm(f => ({ ...f, runtime_minutes: e.target.value }))}
                placeholder="90"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUpload(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection details + appeal */}
      <Dialog open={!!detailUpload} onOpenChange={open => !open && setDetailUpload(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Rejected</DialogTitle>
            <DialogDescription>
              {detailUpload?.metadata?.title || 'Your upload'} was not approved for distribution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-sm font-medium text-destructive">Reviewer notes</p>
              <p className="text-sm text-muted-foreground mt-1">
                {detailUpload?.human_review?.notes || 'No specific notes were provided.'}
              </p>
              {detailUpload?.human_review?.policy_codes?.length ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Policy codes: {detailUpload.human_review.policy_codes.join(', ')}
                </p>
              ) : null}
            </div>
            {detailUpload?.appeal_status === 'requested' ? (
              <p className="text-sm text-muted-foreground">
                Your appeal is pending review.
              </p>
            ) : detailUpload?.appeal_status === 'denied' ? (
              <p className="text-sm text-destructive">Your appeal was denied.</p>
            ) : (
              <div className="space-y-2">
                <Label>Appeal reason</Label>
                <Textarea
                  value={appealReason}
                  onChange={e => setAppealReason(e.target.value)}
                  placeholder="Explain why this decision should be reconsidered..."
                  rows={4}
                  className="resize-none"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailUpload(null)}>Close</Button>
            {detailUpload && !['requested', 'resolved', 'denied'].includes(detailUpload.appeal_status) && (
              <Button onClick={handleSubmitAppeal} disabled={appealSubmitting}>
                {appealSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Submit Appeal
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <AlertDialog open={!!deleteUploadId} onOpenChange={open => !open && setDeleteUploadId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Upload?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the upload record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Information Card */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Review Process</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong>Review Time:</strong> Reviews typically complete within 24-48 hours
          </p>
          <p>
            <strong>Approval:</strong> Approved content will be available on the platform within 2-4 hours
          </p>
          <p>
            <strong>Rejection:</strong> If your content is rejected, you'll receive an explanation and can submit an appeal
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
